import DocumentTitle from 'react-document-title';
import { useLocation, useParams } from 'react-router-dom';
import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';
import { Loader } from '@hivebrite/design-system/Loader';
import { useScrollTop } from '@hivebrite/react-hooks';

import NewsNotFound from 'hb-react/frontoffice/apps/NewsIndex/components/NewsNotFound';
import { useTopicContext } from 'hb-react/frontoffice/contexts/TopicContext';
import { useDefaultAppContexts } from 'hb-react/frontoffice/contexts/withDefaultAppContexts';
import { useNewsPublished } from 'hb-react/shared/apps/News/NewsPublished/hooks/useNewsPublished';
import NewsCategories from 'hb-react/shared/apps/News/NewsPublished/NewsCategories';
import NewsReactions from 'hb-react/shared/apps/News/NewsPublished/NewsReactions';
import NewsRelated from 'hb-react/shared/apps/News/NewsPublished/NewsRelated';
import { useQueryRelatedNews } from 'hb-react/shared/apps/News/NewsPublished/NewsRelated/hooks/useQueryRelatedNews';
import NewsText from 'hb-react/shared/apps/News/NewsPublished/NewsText';
import NewsTitle from 'hb-react/shared/apps/News/NewsPublished/NewsTitle';
import { type PublishedNewsData } from 'hb-react/shared/apps/News/types';
import {
  normalizeRelatedNewsItem,
  normalizeNewsRelatedLetter,
} from 'hb-react/shared/apps/News/utils/normalizeRelatedNews';
import { ContentRecommendation } from 'hb-react/shared/components/ContentRecommendation';
import AttachedFiles from 'hb-react/shared/components/DesignSystem/AttachedFiles';
import PublicationContentWrapper from 'hb-react/shared/components/Publication/PublicationContentWrapper';
import PublicationCoverPicture from 'hb-react/shared/components/Publication/PublicationCoverPicture';
import PublicationLayout from 'hb-react/shared/components/Publication/PublicationLayout';
import useFeatureFlag from 'hb-react/shared/hooks/useFeatureFlag';
import { usePageMenuName, PAGES } from 'hb-react/shared/hooks/usePageMenuName';
import { ENTITY_TYPES } from 'hb-react/shared/utils/entitiesContentData/constants';
import { isCommentsModuleAvailable } from 'hb-react/shared/utils/features/comments';
import { generatePageTitle } from 'hb-react/shared/utils/generatePageTitle';

const NewsPublished = ({ config }: { config: any }) => {
  const { newsId } = useParams();
  const { currentUser } = useDefaultAppContexts();
  const { flagEvNewsRevamp } = useFeatureFlag();
  const { news, isLoading, error } = useNewsPublished(newsId, currentUser?.id);
  const { topicId, whitelabelURL, topicName, whitelabelName } =
    useTopicContext();

  const showRelatedNews = !currentUser;

  const { data: fetchedRelatedNews, isLoading: isLoadingFetchedRelatedNews } =
    useQueryRelatedNews({
      enabled: flagEvNewsRevamp && showRelatedNews,
      topicId,
      whiteLabelTopic: !!whitelabelURL,
      newsId,
    });

  const relatedNews = flagEvNewsRevamp
    ? fetchedRelatedNews.map(normalizeRelatedNewsItem)
    : (news?.relatedLetters || []).map(normalizeNewsRelatedLetter);

  if (isLoading) {
    return <Loader size={50} />;
  }

  if (!newsId || error || !news) {
    return (
      <PublicationLayout>
        <NewsNotFound />
      </PublicationLayout>
    );
  }

  return (
    <>
      <NewsPublishedMain
        news={news}
        config={config}
        topic={{
          id: topicId,
          name: topicName,
          whitelabelActivated: !!whitelabelURL,
          whitelabelName: whitelabelName,
        }}
        withRelatedNews
      />
      {isLoadingFetchedRelatedNews && <Loader size={50} />}
      {showRelatedNews && relatedNews.length > 0 && (
        <NewsRelated relatedNews={relatedNews} />
      )}
    </>
  );
};

export const NewsPublishedMain = ({
  news,
  config,
  withDocumentTitle = true,
  topic = {},
  withRelatedNews = false,
}: {
  config: any;
  news: PublishedNewsData;
  topic?: any;
  withDocumentTitle?: boolean;
  withRelatedNews?: boolean;
}) => {
  const { currentUser, currentNetwork } = useDefaultAppContexts();
  const { flagUjAiContentRecommendation } = useFeatureFlag();

  const {
    attachments,
    categories,
    content,
    coverPicture,
    rssUrl,
    id,
    likes,
    publishedAt,
    socialMediaList = [],
    subject,
  } = news;
  const { pathname } = useLocation();
  // TODO: currently is not supplied by config; check if it's still needed; replace with `new_cover_picture_normal`?
  const defaultCoverPicture = config?.custom?.page?.cover_picture_normal;
  const configPageName = config?.custom?.page?.menu_name;
  const appPagesMenuName = usePageMenuName(PAGES.NEWS, __('News'), {
    enabled: !configPageName,
  });

  // For PO try to get the name from the config.
  const menuName = configPageName || appPagesMenuName;

  // Content recommendation hasn't been implemented for groups yet
  const isContentRecommendationVisible =
    flagUjAiContentRecommendation &&
    currentUser &&
    currentNetwork?.network_configuration?.ai_content_recommendation?.enabled;

  const newsCoverPictureUrl = coverPicture?.previewUrl;

  const files = attachments || [];

  useScrollTop([pathname]);

  return (
    <>
      {withDocumentTitle && (
        <DocumentTitle
          title={generatePageTitle({
            title: subject || menuName || '',
            networkName: currentNetwork?.page_title || currentNetwork.title,
            topicName: topic?.name,
          })}
        />
      )}
      <PublicationLayout pb={withRelatedNews ? '6' : '120px'}>
        <PublicationCoverPicture
          coverPicture={newsCoverPictureUrl}
          defaultCoverPicture={defaultCoverPicture}
          dataTestId="news-published-cover-picture"
        />
        <PublicationContentWrapper>
          <HeaderStyled>
            <NewsTitle
              title={subject}
              publishedAt={publishedAt}
              sharingUrl={`${
                window.location.origin
              }${Routes.network_letter_path(id)}`}
              socialMediaList={socialMediaList}
            />
            <NewsCategories categories={categories} />
          </HeaderStyled>
          <NewsText text={content} externalUrl={rssUrl} />
          <AttachedFiles attachedFiles={files} />
          {isContentRecommendationVisible && (
            <ContentRecommendation
              contentId={typeof id === 'string' ? parseInt(id, 10) : id}
              contentType={ENTITY_TYPES.news}
            />
          )}
          {isCommentsModuleAvailable(
            currentUser,
            !currentNetwork.network_configuration?.news?.comments_enabled,
          ) && <NewsReactions id={id} likes={likes} />}
        </PublicationContentWrapper>
      </PublicationLayout>
    </>
  );
};

export default NewsPublished;

const HeaderStyled = styled(Box).attrs({
  display: 'block',
  mb: 4,
})`
  border-bottom: 1px solid ${({ theme }) => theme.colors.horizon};
`;
